import { useQuery } from '@tanstack/react-query'
import { adminDiagnosticsApi } from '@/api/adminDiagnostics'
import type { DiagnosticReportResponse } from '@/api/adminDiagnostics'
import { resolveRepAssignment } from './useLevelDiagnosticResult'

export type LoadedDiagnosticReport = DiagnosticReportResponse & { assignmentId: string }

/**
 * 원생(userId) 기준 진단 리포트.
 * 대표 assignment(resolveRepAssignment: COMPLETED 우선·roundNo 최대)를 구한 뒤
 * GET /lc/admin/diagnostics/{assignmentId}/report 호출.
 *
 * fail-soft: 진단 미배정(rep=null) → data=null. 403(ENTRANCE_TEST 미보유)/404/미채점은
 * retry 없이 isError 또는 null 데이터로 떨어져 컴포넌트가 안내 문구를 렌더한다.
 * report 는 assignmentId 기준 → 회차별 독립(대표=최신 COMPLETED).
 */
export function useDiagnosticReport(userId: number) {
  return useQuery({
    queryKey: ['lc', 'admin', 'diagnosticReport', userId],
    enabled: Number.isInteger(userId) && userId > 0,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<LoadedDiagnosticReport | null> => {
      const rows = (await adminDiagnosticsApi.list()).data.data ?? []
      const rep = resolveRepAssignment(rows, userId)
      if (!rep || rep.status !== 'COMPLETED') return null
      return { ...(await adminDiagnosticsApi.getReport(rep.id)).data.data, assignmentId: rep.id }
    },
  })
}
